import { BeakerIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showConfirmModal } from '../../components/ui/Modal';
import { PromptGuidance } from '../../components/settings/PromptGuidance';
import { useToast } from '../../components/ui/toast';
import { describeApiError } from '../../data/http';
import { deleteService, listServices, saveService, testService } from '../../data/services';
import type { SaveServiceRequest, ServiceSummary, ServiceTestResult } from '../../data/types';

const blank: SaveServiceRequest = {
  name: '',
  description: '',
  lookupType: 'ApiCall',
  endpointUrl: '',
  method: 'GET',
  headersJson: '{}',
  requestBodyTemplate: '',
  authenticationType: 'None',
  apiKeyHeaderName: '',
  apiKeyLocation: 'Header',
  secretSetting: '',
  managedIdentityResource: '',
  timeoutSeconds: 30,
  systemPrompt: '',
  responseType: 'Text',
  isActive: true
};

const field =
  'mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

/** Mirrors the backend's `{{Property}}` reference syntax — `{{$Var}}` and `{{@Key}}` are system forms, not test inputs. */
const TOKEN_REFERENCE = /\{\{(?![@$])([^{}>]+?)(?:>[^{}]*)?\}\}/g;

/** The distinct `{{Property}}` names a service's config references, so the admin can supply sample values before testing. */
function detectTokenNames(draft: SaveServiceRequest): string[] {
  const sources = [draft.endpointUrl, draft.headersJson, draft.requestBodyTemplate, draft.systemPrompt];
  const names = new Set<string>();
  for (const source of sources) {
    for (const match of source?.matchAll(TOKEN_REFERENCE) ?? []) {
      names.add(match[1].trim());
    }
  }
  return [...names];
}

/** Drops the server-owned fields so the form only holds what it can send back. */
function toDraft(service: ServiceSummary): SaveServiceRequest {
  return {
    name: service.name,
    description: service.description,
    lookupType: service.lookupType,
    endpointUrl: service.endpointUrl,
    method: service.method,
    headersJson: service.headersJson,
    requestBodyTemplate: service.requestBodyTemplate,
    authenticationType: service.authenticationType,
    apiKeyHeaderName: service.apiKeyHeaderName,
    apiKeyLocation: service.apiKeyLocation,
    secretSetting: service.secretSetting,
    managedIdentityResource: service.managedIdentityResource,
    timeoutSeconds: service.timeoutSeconds,
    systemPrompt: service.systemPrompt,
    responseType: service.responseType,
    isActive: service.isActive
  };
}

export function ServicesPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [services, setServices] = useState<ServiceSummary[]>([]);
  const [editing, setEditing] = useState<{ id?: string; draft: SaveServiceRequest } | null>(null);
  const [result, setResult] = useState<ServiceTestResult | null>(null);
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    return listServices()
      .then((items) => {
        setServices(items);
        setError(null);
      })
      .catch((err: unknown) => setError(describeApiError(err, t('settings.services.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (changes: Partial<SaveServiceRequest>) =>
    setEditing((current) => (current ? { ...current, draft: { ...current.draft, ...changes } } : current));

  const save = async () => {
    if (!editing || busy) return;
    setBusy(true);
    try {
      const saved = await saveService(editing.draft, editing.id);
      toast({ title: t('settings.services.saved'), type: 'success' });
      setEditing({ id: saved.id, draft: editing.draft });
      await load();
    } catch (err) {
      toast({ title: describeApiError(err, t('settings.services.saveFailed')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    if (!editing?.id || busy) return;
    setBusy(true);
    setResult(null);
    try {
      setResult(await testService(editing.id, testValues));
    } catch (err) {
      toast({ title: describeApiError(err, t('settings.services.testFailed')), type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (service: ServiceSummary) => {
    const confirmed = await showConfirmModal({
      title: t('settings.services.deleteTitle'),
      description: t('settings.services.deleteDescription', { name: service.name }),
      confirmLabel: t('common.delete'),
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await deleteService(service.id);
      if (editing?.id === service.id) setEditing(null);
      await load();
      toast({ title: t('settings.services.deleted'), type: 'success' });
    } catch (err) {
      toast({ title: describeApiError(err, t('settings.services.deleteFailed')), type: 'error' });
    }
  };

  const isApiCall = editing?.draft.lookupType === 'ApiCall';
  const needsSecret = editing?.draft.authenticationType === 'ApiKey' || editing?.draft.authenticationType === 'Bearer';
  const isApiKeyAuth = editing?.draft.authenticationType === 'ApiKey';
  const apiKeyInQuery = isApiKeyAuth && editing?.draft.apiKeyLocation === 'Query';
  const testTokenNames = editing ? detectTokenNames(editing.draft) : [];

  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-normal text-foreground">
            {t('settings.services.title')}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('settings.services.subtitle')}</p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditing({ draft: { ...blank } });
            setResult(null);
            setTestValues({});
          }}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <PlusIcon className="size-4" aria-hidden="true" />
          {t('settings.services.add')}
        </button>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mt-6 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <section>
            <h2 className="text-base font-semibold text-foreground">{t('settings.services.configured')}</h2>

            {services.length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">{t('settings.services.empty')}</p>
            ) : (
              <ul className="mt-3 grid gap-2">
                {services.map((service) => (
                  <li key={service.id} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-surface p-4">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing({ id: service.id, draft: toDraft(service) });
                          setResult(null);
                          setTestValues({});
                        }}
                        className="font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {service.name}
                      </button>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {service.lookupType === 'AIPrompt'
                          ? t('settings.services.typeAiPrompt')
                          : t('settings.services.typeApiCall')}{' '}
                        · {service.isActive ? t('settings.services.active') : t('settings.services.inactive')}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => void remove(service)}
                      aria-label={t('settings.services.deleteLabel', { name: service.name })}
                      className="shrink-0 rounded border border-input bg-surface p-1.5 text-destructive transition hover:bg-destructive-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      <TrashIcon className="size-4" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {editing ? (
            <section className="rounded-lg border border-border bg-surface p-5">
              <h2 className="text-base font-semibold text-foreground">
                {editing.id ? t('settings.services.editTitle') : t('settings.services.newTitle')}
              </h2>

              <div className="mt-4 grid gap-3">
                <label className="block text-sm">
                  <span className="font-medium text-foreground">{t('settings.services.name')}</span>
                  <input type="text" value={editing.draft.name} onChange={(e) => update({ name: e.target.value })} className={field} />
                </label>

                <label className="block text-sm">
                  <span className="font-medium text-foreground">{t('settings.services.type')}</span>
                  <select
                    value={editing.draft.lookupType}
                    onChange={(e) => {
                      const lookupType = e.target.value as SaveServiceRequest['lookupType'];
                      const clampResponseType =
                        lookupType === 'AIPrompt' && (editing.draft.responseType === 'Image' || editing.draft.responseType === 'Document');
                      update({ lookupType, ...(clampResponseType ? { responseType: 'Text' } : {}) });
                    }}
                    className={field}
                  >
                    <option value="ApiCall">{t('settings.services.typeApiCall')}</option>
                    <option value="AIPrompt">{t('settings.services.typeAiPrompt')}</option>
                  </select>
                </label>

                <label className="block text-sm">
                  <span className="font-medium text-foreground">{t('settings.services.responseType')}</span>
                  <select
                    value={editing.draft.responseType}
                    onChange={(e) => update({ responseType: e.target.value as SaveServiceRequest['responseType'] })}
                    className={field}
                  >
                    <option value="Text">{t('settings.services.responseTypeText')}</option>
                    <option value="Table">{t('settings.services.responseTypeTable')}</option>
                    <option value="List">{t('settings.services.responseTypeList')}</option>
                    {isApiCall ? (
                      <>
                        <option value="Image">{t('settings.services.responseTypeImage')}</option>
                        <option value="Document">{t('settings.services.responseTypeDocument')}</option>
                      </>
                    ) : null}
                  </select>
                  <span className="mt-1 block text-xs text-muted-foreground">{t('settings.services.responseTypeHint')}</span>
                </label>

                {isApiCall ? (
                  <>
                    <label className="block text-sm">
                      <span className="font-medium text-foreground">{t('settings.services.endpoint')}</span>
                      <input type="url" value={editing.draft.endpointUrl} onChange={(e) => update({ endpointUrl: e.target.value })} className={field} />
                    </label>

                    <label className="block text-sm">
                      <span className="font-medium text-foreground">{t('settings.services.auth')}</span>
                      <select
                        value={editing.draft.authenticationType}
                        onChange={(e) => update({ authenticationType: e.target.value as SaveServiceRequest['authenticationType'] })}
                        className={field}
                      >
                        <option value="None">None</option>
                        <option value="ApiKey">ApiKey</option>
                        <option value="Bearer">Bearer</option>
                        <option value="ManagedIdentity">ManagedIdentity</option>
                      </select>
                    </label>

                    {needsSecret ? (
                      <label className="block text-sm">
                        <span className="font-medium text-foreground">{t('settings.services.secretSetting')}</span>
                        <input
                          type="text"
                          value={editing.draft.secretSetting}
                          onChange={(e) => update({ secretSetting: e.target.value })}
                          placeholder="MY_SERVICE_KEY"
                          className={`${field} font-mono`}
                        />
                        <span className="mt-1 block text-xs text-muted-foreground">{t('settings.services.secretHint')}</span>
                      </label>
                    ) : null}

                    {isApiKeyAuth ? (
                      <>
                        <label className="block text-sm">
                          <span className="font-medium text-foreground">{t('settings.services.apiKeyLocation')}</span>
                          <select
                            value={editing.draft.apiKeyLocation}
                            onChange={(e) => update({ apiKeyLocation: e.target.value as SaveServiceRequest['apiKeyLocation'] })}
                            className={field}
                          >
                            <option value="Header">{t('settings.services.apiKeyLocationHeader')}</option>
                            <option value="Query">{t('settings.services.apiKeyLocationQuery')}</option>
                          </select>
                          <span className="mt-1 block text-xs text-muted-foreground">{t('settings.services.apiKeyLocationHint')}</span>
                        </label>

                        <label className="block text-sm">
                          <span className="font-medium text-foreground">
                            {apiKeyInQuery ? t('settings.services.apiKeyParamName') : t('settings.services.apiKeyHeaderName')}
                          </span>
                          <input
                            type="text"
                            value={editing.draft.apiKeyHeaderName}
                            onChange={(e) => update({ apiKeyHeaderName: e.target.value })}
                            placeholder={apiKeyInQuery ? 'token' : 'X-Api-Key'}
                            className={`${field} font-mono`}
                          />
                        </label>
                      </>
                    ) : null}
                  </>
                ) : (
                  <label className="block text-sm">
                    <span className="font-medium text-foreground">{t('settings.services.systemPrompt')}</span>
                    <textarea rows={4} value={editing.draft.systemPrompt} onChange={(e) => update({ systemPrompt: e.target.value })} className={field} />
                  </label>
                )}
                <label className="block text-sm">
                  <span className="font-medium text-foreground">{t('settings.services.bodyTemplate')}</span>
                  <textarea
                    rows={3}
                    value={editing.draft.requestBodyTemplate}
                    onChange={(e) => update({ requestBodyTemplate: e.target.value })}
                    className={`${field} font-mono`}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">{t('settings.services.bodyHint')}</span>
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.draft.isActive}
                    onChange={(e) => update({ isActive: e.target.checked })}
                    className="size-4 rounded border-input"
                  />
                  <span className="text-foreground">{t('settings.services.active')}</span>
                </label>
              </div>

              {editing.id && testTokenNames.length > 0 ? (
                <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
                  <h3 className="text-sm font-semibold text-foreground">{t('settings.services.testValues')}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">{t('settings.services.testValuesHint')}</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {testTokenNames.map((name) => (
                      <label key={name} className="block text-sm">
                        <span className="font-medium text-foreground">{name}</span>
                        <input
                          type="text"
                          value={testValues[name] ?? ''}
                          onChange={(e) => setTestValues((current) => ({ ...current, [name]: e.target.value }))}
                          className={`${field} font-mono`}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => void save()}
                  disabled={busy}
                  className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? t('common.saving') : t('common.save')}
                </button>

                <button
                  type="button"
                  onClick={() => void runTest()}
                  disabled={busy || !editing.id}
                  title={editing.id ? undefined : t('settings.services.saveBeforeTest')}
                  className="inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <BeakerIcon className="size-4" aria-hidden="true" />
                  {t('settings.services.test')}
                </button>
              </div>

              {result ? (
                <div
                  role="status"
                  className={`mt-4 rounded border-l-4 px-4 py-3 text-sm ${
                    result.success ? 'border-border bg-muted text-foreground' : 'border-destructive bg-destructive-soft text-destructive'
                  }`}
                >
                  <p className="font-semibold">
                    {result.success
                      ? t('settings.services.testOk', { ms: result.elapsedMs })
                      : t('settings.services.testBad')}
                  </p>
                  {result.success && result.responseType === 'Image' && result.preview.startsWith('data:') ? (
                    <img
                      src={result.preview}
                      alt={t('settings.services.testImageAlt')}
                      className="mt-2 max-h-40 max-w-full rounded border border-border object-contain"
                    />
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">
                      {result.success ? result.preview : result.error}
                    </p>
                  )}
                </div>
              ) : null}

              {editing.draft.lookupType === 'AIPrompt' ? <PromptGuidance /> : null}
            </section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
